import { useEffect, useState, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PropTypes from 'prop-types';

import { useAuth } from './context/AuthContext.jsx';

import Spinner from './components/home/ui/Spinner.jsx';
import CookieConsentBanner from './components/CookieConsentBanner.jsx';
import ClarityPrivacyGuard from './components/ClarityPrivacyGuard.jsx';
import ScrollToTop from './components/utils/ScrollToTop.jsx';

import AdminRoute from './routes/AdminRoute.jsx';
import ProtectedRoute from './routes/ProtectedRoute.jsx';
import lazyWithRetry from './routes/lazyWithRetry.js';

// Decorativos (Navidad): lazy para que firestore no entre al bundle inicial
const LazySnowEffect          = lazyWithRetry(() => import('./components/SnowEffect.jsx'));
const LazyChristmasGarland    = lazyWithRetry(() => import('./components/ChristmasGarland.jsx'));
const LazyChristmasBackground = lazyWithRetry(() => import('./components/ChristmasBackground.jsx'));
import {
  publicRoutes,
  protectedRoutes,
  adminRoutes,
  LazyNotFoundPage,
  LazyImpersonationBanner,
} from './routes/routeConfig.js';
import ErrorBoundary from './components/ui/ErrorBoundary.jsx';

const LazyMaintenancePage = lazyWithRetry(() => import('./pages/MaintenancePage.jsx'));
const LazyLoginRegister   = lazyWithRetry(() => import('./components/LoginRegister.jsx'));


// ===============================================================
// GUARDIA DE MODO MANTENIMIENTO
// ===============================================================

const MaintenanceGate = ({ children }) => {
  const { currentUser, authLoading } = useAuth();
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);

  useEffect(() => {
    let mounted = true;
    let unsubscribeSettings = () => {};

    (async () => {
      // Dynamic import: Firestore queda fuera del bundle inicial.
      const [{ doc, onSnapshot }, { loadFirestore }] = await Promise.all([
        import('firebase/firestore'),
        import('./services/firebase.js'),
      ]);
      const db = await loadFirestore();
      if (!mounted) return;

      const settingsRef = doc(db, 'settings', 'app');
      unsubscribeSettings = onSnapshot(
        settingsRef,
        (docSnap) => {
          if (!mounted) return;
          if (docSnap.exists()) {
            const data = docSnap.data();
            setIsMaintenanceMode(data.isMaintenanceMode || false);
          } else {
            setIsMaintenanceMode(false);
          }
        },
        (error) => {
          console.error('Error al escuchar settings:', error);
          if (mounted) setIsMaintenanceMode(false);
        }
      );
    })().catch((err) => {
      console.error('Error inicializando MaintenanceGate:', err);
    });

    return () => {
      mounted = false;
      unsubscribeSettings();
    };
  }, []);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!isMaintenanceMode) {
        setCheckingStatus(false);
        return;
      }

      if (!currentUser) {
        setIsAuthorized(false);
        setCheckingStatus(false);
        return;
      }

      try {
        const [{ doc, getDoc }, { loadFirestore }] = await Promise.all([
          import('firebase/firestore'),
          import('./services/firebase.js'),
        ]);
        const db = await loadFirestore();

        const profileRef = doc(db, 'profiles', currentUser.uid);
        const profileSnap = await getDoc(profileRef);

        if (profileSnap.exists() && profileSnap.data().isAdmin === true) {
          setIsAuthorized(true);
        } else {
          setIsAuthorized(false);
        }
      } catch (error) {
        console.error('Error al verificar permisos de admin:', error);
        setIsAuthorized(false);
      } finally {
        setCheckingStatus(false);
      }
    };

    if (!authLoading && isMaintenanceMode !== null) {
      checkAdminStatus();
    }
  }, [currentUser, authLoading, isMaintenanceMode]);

  if (isMaintenanceMode && (authLoading || checkingStatus)) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Spinner text="Verificando permisos..." />
      </div>
    );
  }

  if (isMaintenanceMode && !isAuthorized) {
    return (
      <Suspense fallback={<Spinner text="Cargando..." />}>
        <LazyMaintenancePage />
      </Suspense>
    );
  }

  return (
    <>
      <Suspense fallback={null}>
        <LazyChristmasBackground />
        <LazySnowEffect />
        <LazyChristmasGarland />
      </Suspense>
      {children}
    </>
  );
};


MaintenanceGate.propTypes = { children: PropTypes.node.isRequired };

// ===============================================================
// RUTAS PRINCIPALES (excepto /auth)
// ===============================================================

const AppRoutes = () => {
  const { isImpersonating } = useAuth();

  const routeFallback = (
    <div className="flex justify-center items-center h-screen">
      <Spinner text="Cargando..." />
    </div>
  );

  return (
    <>
      {isImpersonating && (
        <Suspense fallback={null}>
          <LazyImpersonationBanner />
        </Suspense>
      )}

      <div className={isImpersonating ? 'pt-12' : ''}>
        <Suspense fallback={routeFallback}>
          <Routes>
            {/* Rutas públicas generales (landing, legal, pagos, etc.) */}
            {publicRoutes
              .filter(({ path }) => path !== '/p/:slug')
              .map(({ path, component: C }) => (
                <Route key={path} path={path} element={<C />} />
              ))}

            {/* Widget de reserva — boundary propio, es la cara al cliente */}
            <Route element={<ErrorBoundary section="reservas" />}>
              {publicRoutes
                .filter(({ path }) => path === '/p/:slug')
                .map(({ path, component: C }) => (
                  <Route key={path} path={path} element={<C />} />
                ))}
            </Route>

            {/* Dashboard y rutas protegidas */}
            <Route element={<ErrorBoundary section="dashboard" />}>
              {protectedRoutes.map(({ path, component: C }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <ProtectedRoute>
                      <C />
                    </ProtectedRoute>
                  }
                />
              ))}
            </Route>

            {/* Panel de administración */}
            <Route element={<ErrorBoundary section="administración" />}>
              {adminRoutes.map(({ path, component: C }) => (
                <Route
                  key={path}
                  path={path}
                  element={
                    <AdminRoute>
                      <C />
                    </AdminRoute>
                  }
                />
              ))}
            </Route>

            <Route path="*" element={<LazyNotFoundPage />} />
          </Routes>
        </Suspense>
      </div>
    </>
  );
};


// ===============================================================
// COMPONENTE PRINCIPAL APP
// ===============================================================

function App() {
  const globalFallback = (
    <div className="flex justify-center items-center h-screen">
      <Spinner text="Cargando aplicación..." />
    </div>
  );

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />
      <ScrollToTop />

      <ErrorBoundary>
        <Suspense fallback={globalFallback}>
          <Routes>
            <Route path="/auth" element={<LazyLoginRegister />} />

            <Route
              path="/*"
              element={
                <MaintenanceGate>
                  <AppRoutes />
                </MaintenanceGate>
              }
            />
          </Routes>
        </Suspense>
      </ErrorBoundary>

      <CookieConsentBanner />
      <ClarityPrivacyGuard />
    </>
  );
}

export default App;
